/**
 * The Projects list's `.mia/project.json` reads (name, description, technologies the agent wrote), asked only of the
 * projects the person can read and without waking anyone's sandbox:
 * - never for a project shared with them (`sharedRole`): it lives in its owner's pod, and reading it there woke the
 *   owner's sandbox just to show a list (the index already gives its name and description);
 * - never while the person's own sandbox wakes (the page asks again once it is up: every quiet refetch used to send
 *   one read per project again);
 * - at most CONCURRENCY at a time;
 * - a project that answers 401, 403 or 404 is remembered for this browser session and not asked again: a stale entry
 *   of the person's registry (a project deleted, or no longer shared with them), or an auth hiccup at the gateway
 *   (a 401 is first tried once more after a moment: the sandbox gateway and the pod never answer 401 on this path; the
 *   API gateway does, when the person's token is expired or cannot be checked).
 */
import { fetchProjectMetadata } from './vfsClient';

const CONCURRENCY = 4;
const RETRY_401_MS = 1500;
const KEY = 'flow.projects.unreadableMetadata';

function remembered() {
  try {
    return new Set(JSON.parse(sessionStorage.getItem(KEY) || '[]'));
  } catch {
    return new Set();
  }
}

function remember(projectId) {
  try {
    const ids = remembered();
    ids.add(projectId);
    sessionStorage.setItem(KEY, JSON.stringify([...ids].slice(-500)));
  } catch {
    // not remembered: asked again next time
  }
}

/** The projects whose metadata the list may ask for. */
export function readableForMetadata(projects) {
  const skip = remembered();
  return (projects || []).filter((project) => project && project._id && !project.sharedRole && !skip.has(project._id));
}

const wait = (ms) => new Promise((resolve) => { setTimeout(resolve, ms); });

async function readOne(projectId, { retryMs }) {
  let answer = await fetchProjectMetadata(projectId);
  if (answer.status === 401) {
    await wait(retryMs);
    answer = await fetchProjectMetadata(projectId);
  }
  if ([401, 403, 404].includes(answer.status)) { remember(projectId); }
  return answer.metadata;
}

/**
 * Reads the metadata of `projects` (after readableForMetadata), `onMetadata(projectId, metadata)` for each one found;
 * `cancelled()` stops before the next read. Resolves when all are done.
 */
export async function loadListMetadata(projects, { onMetadata, cancelled = () => false, concurrency = CONCURRENCY, retryMs = RETRY_401_MS } = {}) {
  const queue = readableForMetadata(projects).map((project) => project._id);
  const worker = async() => {
    while (queue.length && !cancelled()) {
      const projectId = queue.shift();
      // eslint-disable-next-line no-await-in-loop
      const metadata = await readOne(projectId, { retryMs });
      if (metadata && !cancelled()) { onMetadata(projectId, metadata); }
    }
  };
  await Promise.all(Array.from({ length: Math.min(concurrency, queue.length) }, worker));
}
